import { CloudOff } from "@mui/icons-material";
import { QueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { t } from "i18next";
import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { AppStorage } from "~/src/contexts/StorageContext/StorageProvider";
import { ClientState, Jaktdag, JaktdagResponse } from "../../api/types";
import { sumSettOgSkuttJaktdag } from "../../helpers/jaktdagHelper";
import { getFirstAndLastName } from "../../helpers/stringHelper";
import BottomLine from "../BottomLine";
import CounterChip from "../Chips/CounterChip";
import {
  ArrowRightIcon,
  CompleteIcon,
  ErrorIcon,
  SyncErrorIcon,
  SyncIcon,
  WarningIcon,
} from "../Icons/Icons";
import { detailsItem } from "../SkuttDyrCard/SkuttDyrCard";
import Text, { Header, TextVariant } from "../Text/Text";

type JaktdagCardProps = {
  data: JaktdagResponse;
  syncStatus?: "pending" | "error" | "unconfirmed";
  storage?: AppStorage;
  isLoading?: boolean;
  queryClient: QueryClient;
  clientState: ClientState;
};

type JaktdagCardSummary = {
  label?: "" | "innmark" | "utmark" | null;
  sett: number;
  skutt: number;
  jegere: number;
  timer: number;
};

export default function JaktdagCard({
  data,
  syncStatus,
  storage,
  isLoading,
  queryClient,
  clientState,
}: JaktdagCardProps): JSX.Element {
  const sumSettOgSkutt = sumSettOgSkuttJaktdag(
    (storage && storage.setIndividerForJaktdag(data as Jaktdag)) || data,
  );
  const showOfflineNoData =
    queryClient?.getQueryData(["jaktdagId-" + data.Id, { jaktdagId: data.Id }]) === undefined &&
    storage?.get<Jaktdag>({ id: data.Id }, "Jaktdag") === undefined &&
    clientState != "Online" &&
    import.meta.env.MODE !== "prod";
  const summaries: JaktdagCardSummary[] = [];

  if (data.Innmark) {
    const innmarkSummary: JaktdagCardSummary = {
      jegere: data.AntallJegereInnmark || 0,
      skutt: sumSettOgSkutt.sumSkuttInnmark,
      sett: sumSettOgSkutt.sumSettInnmark ?? 0,
      timer: data.AntallTimerJaktetInnmark || 0,
      label: "innmark",
    };

    summaries.push(innmarkSummary);
  }

  if ((data.Utmark && data.Art === "Hjort") || data.Art !== "Hjort") {
    const defaultSummary: JaktdagCardSummary = {
      jegere: data.AntallJegere || 0,
      skutt: sumSettOgSkutt.sumSkutt || 0,
      sett: sumSettOgSkutt.sumSett ?? 0,
      timer: data.AntallTimerJaktet || 0,
      label: data.Utmark ? "utmark" : null,
    };

    summaries.push(defaultSummary);
  }

  const [firstName, lastName] = getFirstAndLastName(data.RegistrertAv);

  return (
    <NavLink to={"/oversikt/jaktdagdetaljer/" + data.Id}>
      {(syncStatus === "pending" || syncStatus === "error") && (
        <>
          <BottomLine />
          <div
            className={`py-2 px-4 ${
              syncStatus === "pending" ? "bg-ldir-blue-500" : "bg-ldir-red-700"
            } flex gap-2`}
          >
            {syncStatus === "pending" && (
              <>
                <SyncIcon
                  className="text-ldir-blue-900"
                  title="Individ har enda ikke blitt overført til Hjorteviltregisteret"
                />
                <Text>Venter på overføring</Text>
              </>
            )}
            {syncStatus === "error" && (
              <>
                <SyncErrorIcon className="text-ldir-red-900" title="Feil ved overføring" />
                <Text>Feil ved overføring</Text>
              </>
            )}
          </div>
        </>
      )}
      <div
        className={`p-4 text-ldir-brown-900 active:bg-ldir-blue-100 border-y border-ldir-blue-pale bg-ldir-blue-100 ${
          isLoading ? "animate-pulse pointer-events-none" : ""
        }`}
      >
        <div className="flex mb-4 items-center">
          {data.Godkjent && (
            <CompleteIcon className="mr-2" title={t("pages.oversikt.jaktdagenErGodkjent")} />
          )}
          <Header headerLvl={3}>
            {data.Dato && format(data.Dato || "", "dd.MM.", { locale: nb })} {data.Art}
          </Header>
          <div className="ml-auto flex">
            {showOfflineNoData && <CloudOff className="text-ldir-blue-900 mr-2" />}
            <ArrowRightIcon />
          </div>
        </div>
        <div className="flex gap-2">
          {data.UtenforJakttid && (
            <WarningIcon className="mb-2" title={t("pages.oversikt.jaktdagUtenforJakttid")} />
          )}
          {sumSettOgSkutt.isInconsistent && (
            <ErrorIcon
              className="mb-2 text-ldir-red-900"
              title={t("pages.oversikt.inkonsistensSettSkutt")}
            />
          )}
        </div>
        <ul>
          {detailsItem(
            t("components.skuttDyrCard.registrertAv"),
            `${firstName.substring(0, 1)}. ${lastName}.`,
          )}
        </ul>
        {summaries[0].label && <BottomLine />}
        {summaries.map((summary, index) => (
          <div key={index}>
            {summary.label && (
              <Text variant={TextVariant.tiny} className="uppercase mt-1">
                {summary.label}
              </Text>
            )}
            <div className="flex justify-between flex-wrap  bg-[#CFDFE4] border-t border-ldir-blue-pale p-2">
              <CounterChip label={t("components.jaktdagCard.seen")} count={summary.sett} />
              <CounterChip label={t("components.jaktdagCard.shot")} count={summary.skutt} />
              <CounterChip label={t("components.jaktdagCard.hunters")} count={summary.jegere} />
              <CounterChip label={t("components.jaktdagCard.hours")} count={summary.timer} />
            </div>
          </div>
        ))}
      </div>
    </NavLink>
  );
}
